/**
 * The 11Deeds mark: an hourglass whose sand settles into a tick (logo V, "time well spent").
 * The glyph only; the caller's tile gives the colour and the rounded square, and the glyph
 * draws in currentColor with the amber sand fixed. Under 32px the sand and tick drop out so
 * the shape still reads.
 */
const GLASS =
  'M38 29 L82 29 C82 46 66 54 66 60 C66 66 82 74 82 91 L38 91 C38 74 54 66 54 60 C54 54 38 46 38 29 Z';
const TOP = 'M47 37 L73 37 C71 44 63 49 60 53 C57 49 49 44 47 37 Z';
const MOUND = 'M43 88 C47 77 55 72 60 70 C65 72 73 77 77 88 Z';
const TICK = 'M53 81 L58 85 L67 76';
export const SAND = '#e0b44a';

export function BrandGlyph({ simple = false, className }: { simple?: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 120 120" aria-hidden className={className}>
      <rect x="28" y="18" width="64" height="11" rx="5.5" fill="currentColor" />
      <rect x="28" y="91" width="64" height="11" rx="5.5" fill="currentColor" />
      <path
        d={GLASS}
        stroke="currentColor"
        strokeWidth={simple ? 8 : 6}
        strokeLinejoin="round"
        fill="none"
      />
      {!simple && <path d={TOP} fill="currentColor" opacity={0.45} />}
      <path d={MOUND} fill={SAND} />
      {!simple && (
        <path
          d={TICK}
          stroke="#ffffff"
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      )}
    </svg>
  );
}

/**
 * The sign-in screen's mark, played once: the tile pops in, the glass draws, the sand runs
 * and the tick signs it off (styles.css, .brand-intro). Reduced motion shows the end frame.
 */
export function BrandIntro() {
  return (
    <div className="brand-intro flex items-center gap-3">
      <span
        aria-hidden
        className="bi-tile grid size-11 place-items-center rounded-control bg-accent text-on-accent"
      >
        <svg viewBox="0 0 120 120" className="size-full">
          <rect className="bi-bar" x="28" y="18" width="64" height="11" rx="5.5" fill="currentColor" />
          <rect className="bi-bar" x="28" y="91" width="64" height="11" rx="5.5" fill="currentColor" />
          <path
            className="bi-glass"
            pathLength={1}
            d={GLASS}
            stroke="currentColor"
            strokeWidth={6}
            strokeLinejoin="round"
            fill="none"
          />
          <path className="bi-top" d={TOP} fill="currentColor" opacity={0.45} />
          <rect className="bi-stream" x="58.75" y="52" width="2.5" height="34" rx="1.25" fill={SAND} />
          <path className="bi-mound" d={MOUND} fill={SAND} />
          <path
            className="bi-tick"
            pathLength={1}
            d={TICK}
            stroke="#ffffff"
            strokeWidth={4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </span>
      <span className="bi-word font-display text-xl font-semibold">11Deeds</span>
    </div>
  );
}

/**
 * The loading hourglass: the sand runs down, then the glass turns over (styles.css,
 * .hourglass). Drawn in currentColor so it fits a button, a panel or the dark chrome.
 */
export function Hourglass({ size = 16, className }: { size?: number; className?: string }) {
  const simple = size < 24;
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      aria-hidden
      className={`hourglass shrink-0 ${className ?? ''}`}
    >
      <g className="hg-glass">
        <rect x="26" y="16" width="68" height="13" rx="6.5" fill="currentColor" />
        <rect x="26" y="91" width="68" height="13" rx="6.5" fill="currentColor" />
        <path
          d={GLASS}
          stroke="currentColor"
          strokeWidth={simple ? 10 : 7}
          strokeLinejoin="round"
          fill="none"
        />
        <path className="hg-top" d={TOP} fill={SAND} />
        {!simple && (
          <rect className="hg-stream" x="58.5" y="52" width="3" height="36" rx="1.5" fill={SAND} />
        )}
        <path className="hg-mound" d={MOUND} fill={SAND} />
      </g>
    </svg>
  );
}
